import { useState } from 'react';
import Swal from 'sweetalert2';
import { Invoice } from '../types';
import invoiceService from '../services/invoiceService';

export interface PaymentData {
  paymentMethod: string;
  transactionId?: string;
  paidAmount?: number;
  isPartialPayment?: boolean;
  notes?: string;
  paymentDate?: string;
  selectedCredits?: number[];
  creditDiscount?: number;
  excessAmount?: number;
  shouldCreateCredit?: boolean;
  creditType?: string;
  creditDescription?: string;
  valor_acrescimo?: number; // Soma juros + multa - evita criar crédito quando excedente é acréscimo
  desconto?: number; // Desconto manual aplicado no pagamento
  motivoDescontoId?: number; // ID do motivo do desconto
}

export interface UsePaymentHandlerReturn {
  paymentLoading: boolean;
  handleReceivePayment: (paymentData: PaymentData, selectedInvoice: Invoice | null, callbacks: PaymentCallbacks) => Promise<void>;
  handleReceivePaymentFromTable: (invoice: Invoice, setSelectedInvoice: (invoice: Invoice) => void, setDialogOpen: (open: boolean) => void) => void;
  handleMarkAsSimplePaid: (invoice: Invoice, setSelectedInvoice: (invoice: Invoice) => void, setDialogOpen: (open: boolean) => void) => void;
}

export interface PaymentCallbacks {
  showAlert: (type: 'success' | 'error', message: string) => void;
  loadInvoices: () => Promise<void>;
  closeDialog: () => void;
  resetSelectedInvoice: () => void;
}

/**
 * Hook centralizado para gerenciar recebimento de pagamentos
 * Elimina duplicidade entre InvoicesPage, ClientInvoices e ContractInvoices
 */
export const usePaymentHandler = (): UsePaymentHandlerReturn => {
  const [paymentLoading, setPaymentLoading] = useState(false);

  /**
   * Função principal para processar pagamentos
   * Centraliza a lógica que estava duplicada em 3 componentes
   */
  const handleReceivePayment = async (
    paymentData: PaymentData,
    selectedInvoice: Invoice | null,
    callbacks: PaymentCallbacks
  ): Promise<void> => {
    console.log('🎯 usePaymentHandler - handleReceivePayment chamado com:', paymentData);
    console.log('📋 SelectedInvoice atual:', selectedInvoice);
    
    if (!selectedInvoice) {
      console.log('❌ Nenhuma fatura selecionada');
      callbacks.showAlert('error', 'Erro: Nenhuma fatura selecionada para pagamento');
      return;
    }

    try {
      console.log('⏳ Iniciando processamento do pagamento...');
      setPaymentLoading(true);
      
      // Usar apenas o método receberPagamento (unificado)
      console.log('🔧 Usando método receberPagamento unificado');
      console.log('💳 Dados do pagamento sendo enviados:', {
        paymentMethod: paymentData.paymentMethod,
        transactionId: paymentData.transactionId,
        paidAmount: paymentData.paidAmount,
        notes: paymentData.notes,
        paymentDate: paymentData.paymentDate,
        selectedCredits: paymentData.selectedCredits,
        creditDiscount: paymentData.creditDiscount,
        excessAmount: paymentData.excessAmount,
        shouldCreateCredit: paymentData.shouldCreateCredit,
        creditType: paymentData.creditType,
        creditDescription: paymentData.creditDescription
      });
      
      const response = await invoiceService.receberPagamento(selectedInvoice.id, {
        paymentMethod: paymentData.paymentMethod,
        transactionId: paymentData.transactionId,
        paidAmount: paymentData.paidAmount,
        notes: paymentData.notes,
        paymentDate: paymentData.paymentDate,
        selectedCredits: paymentData.selectedCredits,
        creditDiscount: paymentData.creditDiscount,
        excessAmount: paymentData.excessAmount,
        shouldCreateCredit: paymentData.shouldCreateCredit,
        creditType: paymentData.creditType,
        creditDescription: paymentData.creditDescription,
        valor_acrescimo: paymentData.valor_acrescimo,
        desconto: paymentData.desconto,
        motivoDescontoId: paymentData.motivoDescontoId
      });
      
      console.log('📡 Resposta da API:', response);
      
      if (response.success) {
        // Calcular valor total da fatura
        const totalInvoiceValue = invoiceService.calculateTotal(
          Number(selectedInvoice.valor) || 0,
          Number(selectedInvoice.acrescimo) || 0,
          Number(selectedInvoice.desconto) || 0
        );
        
        // Calcular valor total aplicado (pagamento + créditos)
        const totalPaidAmount = (paymentData.paidAmount || 0) + (paymentData.creditDiscount || 0);
        
        // Determinar se é pagamento parcial baseado no valor total aplicado
        const isPartial = totalPaidAmount < totalInvoiceValue;
        
        console.log('✅ Pagamento processado com sucesso!', {
          totalInvoiceValue,
          paidAmount: paymentData.paidAmount,
          creditDiscount: paymentData.creditDiscount,
          totalPaidAmount,
          isPartial
        });
        
        callbacks.showAlert(
          'success', 
          isPartial ? 'Pagamento parcial registrado com sucesso!' : 'Pagamento registrado com sucesso!'
        );
        
        await callbacks.loadInvoices();
        callbacks.closeDialog();
        callbacks.resetSelectedInvoice();
      } else {
        console.log('❌ Resposta da API indicou falha:', response);
        callbacks.showAlert('error', response.message || 'Erro ao processar pagamento');
      }
    } catch (error: any) {
      console.log('💥 Erro capturado:', error);
      
      // Verificar se é erro de caixa financeiro
      const caixaErrorMessage = error.response?.data?.message || error.response?.data?.error;
      const isCaixaError = error.response?.status === 400 && 
          caixaErrorMessage?.toLowerCase().includes('caixa');
      
      if (isCaixaError) {
        const mensagemErro = error.response?.data?.errors?.[0] || caixaErrorMessage || 'Caixa financeiro não configurado.';
        
        // Usar SweetAlert para erro de caixa
        Swal.fire({
          icon: 'error',
          title: 'Erro na Configuração do Caixa',
          html: `
            <div style="text-align: left;">
              <p><strong>Não foi possível processar o pagamento:</strong></p>
              <p style="color: #dc3545;">${mensagemErro}</p>
              <br>
              <p style="font-size: 0.9rem; color: #6c757d;">
                <strong>Soluções:</strong><br>
                • Configure um caixa padrão do sistema<br>
                • Vincule um caixa específico ao usuário<br>
                • Entre em contato com o administrador
              </p>
            </div>
          `,
          width: '600px',
          confirmButtonText: 'Entendi',
          confirmButtonColor: '#dc3545',
          showCancelButton: true,
          cancelButtonText: 'Configurar Caixa',
          cancelButtonColor: '#28a745',
          customClass: {
            container: 'swal-z-index-high'
          },
          didOpen: () => {
            // Garantir z-index muito alto para ficar acima dos modais do MUI
            const swalContainer = document.querySelector('.swal2-container') as HTMLElement;
            if (swalContainer) {
              swalContainer.style.zIndex = '999999';
            }
          }
        }).then((result) => {
          if (result.dismiss === Swal.DismissReason.cancel) {
            // Redirecionar para página de configuração de caixa
            window.location.href = '/caixa-financeiro';
          }
        });
      } else {
        // Para outros erros, usar o alert normal
        callbacks.showAlert('error', error.message || 'Erro ao registrar pagamento');
      }
    } finally {
      console.log('🏁 Finalizando processamento...');
      setPaymentLoading(false);
    }
  };

  /**
   * Handler para abrir dialog de pagamento completo a partir da tabela
   */
  const handleReceivePaymentFromTable = (
    invoice: Invoice,
    setSelectedInvoice: (invoice: Invoice) => void,
    setDialogOpen: (open: boolean) => void
  ): void => {
    console.log('🎯 Abrindo dialog de pagamento para fatura:', invoice);
    setSelectedInvoice(invoice);
    setDialogOpen(true);
  };

  /**
   * Handler para abrir dialog de pagamento simples
   */
  const handleMarkAsSimplePaid = (
    invoice: Invoice,
    setSelectedInvoice: (invoice: Invoice) => void,
    setDialogOpen: (open: boolean) => void
  ): void => {
    console.log('🎯 Abrindo dialog simples de pagamento para fatura:', invoice);
    setSelectedInvoice(invoice);
    setDialogOpen(true);
  };

  return {
    paymentLoading,
    handleReceivePayment,
    handleReceivePaymentFromTable,
    handleMarkAsSimplePaid
  };
}; 